import { type ReactElement, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";

import {
  useAdminActivity,
  useAdminSettings,
  useAdminUsers,
  useApproveUser,
  useAuth,
  useDeleteUser,
  useRejectUser,
  useRequestUpdate,
  useResetLink,
  useSaveSiteSettings,
  useSetAdmin,
  useSetDisabled,
  useUpdateStatus,
} from "../api/hooks";
import type { AdminUser, ResetLink, SiteSettings, UpdateStatus } from "../api/types";
import { errorText, useFlash } from "../components/Flash";
import { LoadError, Loading } from "../components/Layout";

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "Never";
const STATUS: Record<AdminUser["status"], string> = {
  active: "Active",
  pending: "Waiting",
  disabled: "Disabled",
};

/** Running the site: sign-up requests, accounts, reset links, who can sign up, what admins have done, updates. */
export function AdminPage() {
  const me = useAuth().data?.user;
  const q = useAdminUsers(Boolean(me?.is_admin));
  if (me && !me.is_admin) return <Navigate to="/" replace />;
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  const pending = q.data.filter((u) => u.status === "pending");
  const accounts = q.data.filter((u) => u.status !== "pending");

  return (
    <>
      <div className="page-head">
        <h1>Admin</h1>
      </div>
      <p className="small muted admin-note">
        Admins manage accounts. Nobody, admins included, can see anyone else&apos;s planner, jobs or balances.
      </p>
      {pending.length > 0 && <Requests users={pending} />}
      <Accounts users={accounts} meId={me?.id ?? 0} />
      <Updates />
      <div className="two-col" style={{ marginTop: "1.25rem" }}>
        <SignUps />
        <Activity />
      </div>
    </>
  );
}

function useNotify() {
  const flash = useFlash();
  return (ok?: string) => ({
    onSuccess: () => ok && flash(ok),
    onError: (err: unknown) => flash(errorText(err), "error"),
  });
}

function Requests({ users }: { users: AdminUser[] }) {
  const approve = useApproveUser();
  const reject = useRejectUser();
  const notify = useNotify();
  return (
    <section className="panel admin-requests" aria-labelledby="req-h">
      <h2 id="req-h">
        Sign-up requests <span className="count-badge">{users.length}</span>
      </h2>
      <ul className="request-list">
        {users.map((u) => (
          <li key={u.id}>
            <div>
              <b>{u.display_name}</b>
              <span className="muted small">
                {u.email} · asked {when(u.created_at)}
              </span>
            </div>
            <div className="actions">
              <button
                className="btn small"
                type="button"
                disabled={approve.isPending}
                onClick={() => approve.mutate(u.id, notify(`${u.display_name} can now sign in.`))}
              >
                Approve
              </button>
              <button
                className="btn small quiet"
                type="button"
                disabled={reject.isPending}
                onClick={() =>
                  window.confirm(`Turn down ${u.display_name}'s sign-up? They can ask again.`) &&
                  reject.mutate(u.id, notify(`Turned down ${u.display_name}.`))
                }
              >
                Turn down
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Accounts({ users, meId }: { users: AdminUser[]; meId: number }) {
  const setDisabled = useSetDisabled();
  const setAdmin = useSetAdmin();
  const resetLink = useResetLink();
  const del = useDeleteUser();
  const notify = useNotify();
  const flash = useFlash();
  const [link, setLink] = useState<{ user: AdminUser; link: ResetLink } | null>(null);

  const makeLink = (u: AdminUser) =>
    resetLink.mutate(u.id, {
      onSuccess: (l) => setLink({ user: u, link: l }),
      onError: (err) => flash(errorText(err), "error"),
    });
  const remove = (u: AdminUser) => {
    const typed = window.prompt(
      `Delete ${u.display_name} and ALL of their data? This can't be undone.\n\nType ${u.email} to confirm.`,
    );
    if (typed === null) return;
    del.mutate({ id: u.id, confirm_email: typed }, notify(`Deleted ${u.display_name} and their data.`));
  };

  return (
    <section className="panel" aria-labelledby="acc-h" style={{ marginTop: "1.25rem" }}>
      <h2 id="acc-h">Accounts</h2>
      {link && <LinkBox {...link} onClose={() => setLink(null)} />}
      <div className="table-wrap">
        <table className="admin-users">
          <thead>
            <tr>
              <th>Name</th>
              <th className="opt">Email</th>
              <th>Status</th>
              <th className="opt">Last signed in</th>
              <th>
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const me = u.id === meId;
              return (
                <tr key={u.id} className={u.status === "disabled" ? "is-disabled" : undefined}>
                  <td>
                    <b>{u.display_name}</b>
                    {me && <span className="pill">You</span>}
                    {u.is_admin && <span className="pill admin">Admin</span>}
                    <span className="phone-only muted small">{u.email}</span>
                  </td>
                  <td className="opt">{u.email}</td>
                  <td>
                    <span className={`status-pill ${u.status}`}>{STATUS[u.status]}</span>
                    {u.reset_link_until && (
                      <span className="muted small" title={`Works until ${when(u.reset_link_until)}`}>
                        {" "}
                        · reset link out
                      </span>
                    )}
                  </td>
                  <td className="muted opt">{when(u.last_login_at)}</td>
                  <td className="admin-actions">
                    <button className="btn small quiet" type="button" onClick={() => makeLink(u)}>
                      Reset link
                    </button>
                    {!me && (
                      <button
                        className="btn small quiet"
                        type="button"
                        onClick={() =>
                          setDisabled.mutate(
                            { id: u.id, value: u.status === "active" },
                            notify(
                              u.status === "active"
                                ? `${u.display_name} is disabled and signed out.`
                                : `${u.display_name} can sign in again.`,
                            ),
                          )
                        }
                      >
                        {u.status === "active" ? "Disable" : "Enable"}
                      </button>
                    )}
                    {u.status === "active" && (
                      <button
                        className="btn small quiet"
                        type="button"
                        onClick={() =>
                          (u.is_admin ||
                            window.confirm(
                              `Make ${u.display_name} an admin? They'll be able to manage every account.`,
                            )) &&
                          setAdmin.mutate(
                            { id: u.id, value: !u.is_admin },
                            notify(
                              u.is_admin
                                ? `${u.display_name} is no longer an admin.`
                                : `${u.display_name} is now an admin.`,
                            ),
                          )
                        }
                      >
                        {u.is_admin ? "Remove admin" : "Make admin"}
                      </button>
                    )}
                    {!me && (
                      <button className="btn small danger" type="button" onClick={() => remove(u)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** A new reset link: shown once, to copy and send. */
function LinkBox({ user, link, onClose }: { user: AdminUser; link: ResetLink; onClose: () => void }) {
  const url = window.location.origin + link.path;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="link-box" role="status">
      <p>
        <b>Reset link for {user.display_name}.</b> Send it to them yourself: it works once, until{" "}
        {when(link.expires_at)}, and this is the only time it&apos;s shown.
      </p>
      <div className="link-row">
        <input readOnly value={url} aria-label="Reset link" onFocus={(e) => e.target.select()} />
        <button className="btn small" type="button" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
        <button className="btn small quiet" type="button" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

function SignUps() {
  const q = useAdminSettings();
  const save = useSaveSiteSettings();
  const notify = useNotify();
  const mode = q.data?.registration;
  const choose = (registration: SiteSettings["registration"]) =>
    save.mutate(
      { registration },
      notify(registration === "closed" ? "Sign-ups closed." : "Sign-ups open, with approval."),
    );
  return (
    <section className="panel" aria-labelledby="signup-h">
      <h2 id="signup-h">Sign-ups</h2>
      <fieldset className="choice" disabled={!q.data || save.isPending}>
        <legend className="visually-hidden">Who can sign up</legend>
        <label className="check">
          <input type="radio" name="reg" checked={mode === "approval"} onChange={() => choose("approval")} />
          Anyone can ask for an account; an admin approves it
        </label>
        <label className="check">
          <input type="radio" name="reg" checked={mode === "closed"} onChange={() => choose("closed")} />
          Closed: nobody new can sign up
        </label>
      </fieldset>
    </section>
  );
}

function Activity() {
  const q = useAdminActivity();
  const [all, setAll] = useState(false);
  const rows = q.data ?? [];
  const shown = all ? rows : rows.slice(0, 8);
  return (
    <section className="panel" aria-labelledby="act-h">
      <h2 id="act-h">Activity</h2>
      {rows.length ? (
        <ul className="activity">
          {shown.map((e) => (
            <li key={e.id}>
              <span>{e.detail}</span>
              <time className="muted small" dateTime={e.at ?? undefined}>
                {when(e.at)}
              </time>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Nothing yet.</p>
      )}
      {rows.length > shown.length && (
        <button type="button" className="linkish small" onClick={() => setAll(true)}>
          Show all {rows.length}
        </button>
      )}
    </section>
  );
}

/** Updating the server to the newest release. The server's updater does the work (deploy/update.sh). */
function Updates() {
  const q = useUpdateStatus();
  const ask = useRequestUpdate();
  const notify = useNotify();
  const s = q.data;

  // Once the new version answers, reload to get its pages too.
  const startedOn = useRef<string | null>(null);
  useEffect(() => {
    if (!s) return;
    if (startedOn.current === null) startedOn.current = s.running;
    else if (s.running !== startedOn.current) window.location.reload();
  }, [s]);

  const update = (to: string) =>
    window.confirm(
      `Update the server to ${to}?\n\nThe database is backed up first. The site is unavailable for a minute or two while it restarts, for everyone.`,
    ) && ask.mutate("update", notify());

  return (
    <section className="panel updates" aria-labelledby="upd-h" style={{ marginTop: "1.25rem" }}>
      <h2 id="upd-h">Updates</h2>
      {!s ? (
        <p className="muted">{q.error ? "Couldn't read the update status." : "Loading..."}</p>
      ) : !s.ready ? (
        <p className="muted">
          This server runs v{s.running}. Updating from here isn&apos;t set up on it: run{" "}
          <code>sudo deploy/install-updater.sh</code> on the server (see DEPLOYMENT.md).
        </p>
      ) : (
        <UpdateBody
          s={s}
          restarting={q.isError}
          asking={ask.isPending}
          onCheck={() => ask.mutate("check", notify())}
          onUpdate={update}
        />
      )}
    </section>
  );
}

function UpdateBody({
  s,
  restarting,
  asking,
  onCheck,
  onUpdate,
}: {
  s: UpdateStatus;
  restarting: boolean;
  asking: boolean;
  onCheck: () => void;
  onUpdate: (to: string) => void;
}) {
  const run = s.update;
  const busy = Boolean(s.requested) || run?.state === "running";
  return (
    <>
      <p>
        This server runs <b>v{s.running}</b>.{" "}
        {s.update_available ? (
          <>
            <b>{s.latest}</b> is out.
          </>
        ) : (
          s.latest && <>That&apos;s the newest release.</>
        )}{" "}
        <span className="muted small">Checked {when(s.checked_at)}.</span>
      </p>

      {busy && (
        <div className="link-box" role="status">
          <p>
            <b>
              {s.requested === "check"
                ? "Checking for a new release..."
                : run?.state === "running"
                  ? `Updating to ${run.target || "the newest release"}: ${run.message.toLowerCase()}...`
                  : "Starting the update..."}
            </b>
            {s.requested !== "check" &&
              (restarting
                ? " The site is restarting; this page reloads when it's back."
                : " It takes a few minutes. The site restarts near the end, and this page reloads when it's back.")}
          </p>
        </div>
      )}
      {!busy && run?.state === "failed" && (
        <div className="update-failed" role="alert">
          <b>The last update didn&apos;t work.</b> {run.message}
        </div>
      )}
      {!busy && run?.state === "done" && (
        <p className="muted small">
          Last update: {run.previous} to {run.target}, {when(run.finished_at)}.
        </p>
      )}

      {s.update_available && s.whats_new.length > 0 && (
        <div className="whats-new">
          {s.whats_new.map((r) => (
            <div key={r.version}>
              <h3>What&apos;s new in {r.version}</h3>
              <Notes text={r.notes} />
            </div>
          ))}
        </div>
      )}

      <div className="actions">
        {s.update_available && s.latest && (
          <button
            className="btn"
            type="button"
            disabled={busy || asking}
            onClick={() => onUpdate(s.latest ?? "")}
          >
            Update to {s.latest}
          </button>
        )}
        <button className="btn quiet" type="button" disabled={busy || asking} onClick={onCheck}>
          Check now
        </button>
      </div>

      {s.log && (
        <details className="update-log">
          <summary>Log of the last run</summary>
          <pre>{s.log}</pre>
        </details>
      )}
    </>
  );
}

/** A CHANGELOG section: "### Heading" lines and "- item" lists; the rest as paragraphs. */
function Notes({ text }: { text: string }) {
  const plain = (line: string) => line.replace(/\*\*|`/g, "");
  const blocks: ReactElement[] = [];
  let items: string[] = [];
  const flush = () => {
    if (items.length)
      blocks.push(
        <ul key={blocks.length}>
          {items.map((it, i) => (
            <li key={i}>{plain(it)}</li>
          ))}
        </ul>,
      );
    items = [];
  };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (line.startsWith("- ")) items.push(line.slice(2));
    else if (items.length && line && raw.startsWith("  ")) items[items.length - 1] += ` ${line}`;
    else {
      flush();
      if (line.startsWith("#")) blocks.push(<h4 key={blocks.length}>{plain(line.replace(/^#+\s*/, ""))}</h4>);
      else if (line) blocks.push(<p key={blocks.length}>{plain(line)}</p>);
    }
  }
  flush();
  return <>{blocks}</>;
}
